import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import {
  Box,
  Dialog,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Organizations } from "@knowfully/ui.common";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import AccreditationChain from "./AccreditationChain";
import LicensureBodyForm from "./LicensureBodyForm";

const LicensureBodyDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    licensureBodyService,
    licensureBodiesUrl,
    licensureBodySkusUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [editOpen, editOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;

  const licensureBodyResult = useStrictQuery(
    ["licensureBodyService.getLicensureBodyById", id, organizationId],
    () => licensureBodyService.getLicensureBodyById(id!),
    {
      enabled: !!id,
    },
  );

  const licensureBody = licensureBodyResult.data?.data;
  useTitle(licensureBody?.name);

  const onLicensureBodyUpdated = () => {
    queryClient.invalidateQueries([
      "licensureBodyService.getLicensureBodyById",
    ]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "licensureBodyUpdated" })),
    );
    editOpenActions.setFalse();
  };

  const rows = [
    RowElement(
      "nameLabel",
      licensureBody?.name!,
      licensureBodyResult.isLoading,
    ),
    RowElement(
      "acronymLabel",
      licensureBody?.acronym ?? "-",
      licensureBodyResult.isLoading,
    ),
    RowElement(
      "descriptionLabel",
      licensureBody?.description ?? "-",
      licensureBodyResult.isLoading,
    ),
  ];

  if (!id) return <Navigate to={licensureBodiesUrl()} replace />;
  if (licensureBodyResult.isLoading) return <Loader />;

  if (!licensureBody) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={licensureBodiesUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">{t({ id: "licensureBodyNotAccessible" })}</Alert>
      </>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={licensureBodiesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>{licensureBody.name}</h1>

      <Box mb={3} display="flex" justifyContent="center" gap={4}>
        <Tooltip title={t({ id: "editLicensureBody" })}>
          <Fab
            color="primary"
            onClick={editOpenActions.setTrue}
            disabled={!isRootOrganization}
          >
            <EditIcon />
          </Fab>
        </Tooltip>
        <Tooltip title={t({ id: "associatedSkus" })}>
          <Fab
            color="primary"
            onClick={() => navigate(licensureBodySkusUrl(id))}
          >
            <LocalLibraryIcon />
          </Fab>
        </Tooltip>
      </Box>

      <Typography variant="h5" sx={{ margin: "20px 0" }}>
        {t({ id: "licensureBody" })}
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row" width="30%">
                  <b>{title}</b>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AccreditationChain items={licensureBody.accreditationChain} />

      <Dialog open={editOpen} disableEnforceFocus>
        <LicensureBodyForm
          licensureBodyId={id}
          licensureBody={licensureBody}
          title={t({ id: "editLicensureBody" })}
          onSubmit={onLicensureBodyUpdated}
          onCancel={editOpenActions.setFalse}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default LicensureBodyDetails;
